import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import type {GroupedPermissionSection} from '../core/permissionGrouping'
import type {AccessGrantEffect} from '../core/accessRequests'

export type PermissionBulkCheckboxConfig<T> = {
  getItemCode: (item: T) => string
  isSelected: (code: string) => boolean
  onSetMany: (codes: string[], selected: boolean) => void
  disabled?: boolean
  moduleCountLegend: string
}

export type PermissionGlobalBulkConfig = {
  totalCatalogCount: number
  selectedGlobalCount: number
  legend: string
  disabled?: boolean
}

/** User override matrix: per-module allow/deny actions (no single master checkbox). */
export type PermissionOverrideModuleBulkConfig<T> = {
  getEffect: (item: T) => AccessGrantEffect
  disabled?: boolean
  onAllowAllVisibleInModule: (items: T[]) => void
  onClearAllowInModule: (items: T[]) => void
  onDenyAllVisibleInModule: (items: T[]) => void
  onClearDenyInModule: (items: T[]) => void
}

export function IndeterminateCheckbox({
  checked,
  indeterminate,
  onChange,
  disabled,
  title,
  id,
  className,
  style,
}: {
  checked: boolean
  indeterminate: boolean
  onChange: () => void
  disabled?: boolean
  title?: string
  id?: string
  /** e.g. form-check-input plus size overrides */
  className?: string
  style?: React.CSSProperties
}) {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate
    }
  }, [indeterminate])

  return (
    <input
      ref={ref}
      id={id}
      type='checkbox'
      className={className ?? 'form-check-input m-0 flex-shrink-0'}
      style={style}
      checked={checked}
      disabled={disabled}
      title={title}
      aria-checked={indeterminate ? 'mixed' : checked}
      onChange={(e) => {
        e.stopPropagation()
        onChange()
      }}
      onClick={(e) => e.stopPropagation()}
    />
  )
}

export type CollapsiblePermissionGroupsProps<T> = {
  groups: GroupedPermissionSection<T>[]
  searchQuery: string
  getItemKey: (item: T) => string
  renderGroupHeaderRight?: (moduleKey: string, items: T[]) => React.ReactNode
  renderItem: (item: T) => React.ReactNode
  emptyMessage?: string
  /** When true and search is empty, expand every section (default: false = all collapsed). */
  startAllExpanded?: boolean
  bulkCheckbox?: PermissionBulkCheckboxConfig<T>
  globalBulk?: PermissionGlobalBulkConfig
  /** Grouped user overrides: per-module allow/deny bulk actions (mutually exclusive effects per row). */
  overrideModuleBulk?: PermissionOverrideModuleBulkConfig<T>
}

export function CollapsiblePermissionGroups<T>({
  groups,
  searchQuery,
  getItemKey,
  renderGroupHeaderRight,
  renderItem,
  emptyMessage = 'No items match.',
  startAllExpanded = false,
  bulkCheckbox,
  globalBulk,
  overrideModuleBulk,
}: CollapsiblePermissionGroupsProps<T>) {
  const groupsFingerprint = useMemo(
    () => groups.map((g) => `${g.moduleKey}:${g.items.length}`).join('|'),
    [groups]
  )

  const groupsRef = useRef(groups)
  groupsRef.current = groups

  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())

  useEffect(() => {
    const g = groupsRef.current
    const q = searchQuery.trim()
    if (!q) {
      setExpanded(startAllExpanded ? new Set(g.map((x) => x.moduleKey)) : new Set())
      return
    }
    const next = new Set<string>()
    for (const section of g) {
      if (section.items.length > 0) next.add(section.moduleKey)
    }
    setExpanded(next)
  }, [searchQuery, groupsFingerprint, startAllExpanded])

  const toggle = useCallback((k: string) => {
    setExpanded((prev) => {
      const n = new Set(prev)
      if (n.has(k)) n.delete(k)
      else n.add(k)
      return n
    })
  }, [])

  const visibleCodes = useMemo(() => {
    if (!bulkCheckbox) return [] as string[]
    const codes: string[] = []
    const seen = new Set<string>()
    for (const g of groups) {
      for (const item of g.items) {
        const c = bulkCheckbox.getItemCode(item).trim().toUpperCase()
        if (c && !seen.has(c)) {
          seen.add(c)
          codes.push(c)
        }
      }
    }
    return codes
  }, [groups, bulkCheckbox])

  const bulkScopeDescription = useMemo(() => {
    const q = searchQuery.trim()
    if (!q || !bulkCheckbox) return null
    return `Bulk action applies to filtered results (${visibleCodes.length} visible).`
  }, [searchQuery, visibleCodes.length, bulkCheckbox])

  const overrideBulkScopeDescription = useMemo(() => {
    const q = searchQuery.trim()
    if (!q || !overrideModuleBulk) return null
    const n = groups.reduce((s, g) => s + g.items.length, 0)
    return `Module actions apply to filtered results only (${n} visible row(s)).`
  }, [searchQuery, overrideModuleBulk, groups])

  const visibleSelectedCount = useMemo(() => {
    if (!bulkCheckbox) return 0
    return visibleCodes.filter((c) => bulkCheckbox.isSelected(c)).length
  }, [visibleCodes, bulkCheckbox])

  const allVisibleSelected =
    visibleCodes.length > 0 && visibleSelectedCount === visibleCodes.length
  const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected

  const applyVisibleSelectAll = useCallback(() => {
    if (!bulkCheckbox || bulkCheckbox.disabled) return
    bulkCheckbox.onSetMany(visibleCodes, true)
  }, [bulkCheckbox, visibleCodes])

  const applyVisibleUnselectAll = useCallback(() => {
    if (!bulkCheckbox || bulkCheckbox.disabled) return
    bulkCheckbox.onSetMany(visibleCodes, false)
  }, [bulkCheckbox, visibleCodes])

  const toggleGlobalBulk = useCallback(() => {
    if (!bulkCheckbox || bulkCheckbox.disabled) return
    if (allVisibleSelected) {
      bulkCheckbox.onSetMany(visibleCodes, false)
    } else {
      bulkCheckbox.onSetMany(visibleCodes, true)
    }
  }, [bulkCheckbox, allVisibleSelected, visibleCodes])

  const totalItems = useMemo(() => groups.reduce((s, g) => s + g.items.length, 0), [groups])

  if (totalItems === 0) {
    return (
      <div className='text-center text-muted py-5 border rounded bg-light'>
        {emptyMessage}
      </div>
    )
  }

  return (
    <div className='d-flex flex-column gap-2'>
      {overrideModuleBulk && overrideBulkScopeDescription ? (
        <div className='small text-primary py-1 px-2' role='note'>
          {overrideBulkScopeDescription}
        </div>
      ) : null}

      {bulkCheckbox && globalBulk && (
        <div className='d-flex flex-wrap align-items-center gap-2 gap-md-3 py-2 px-2 px-md-3 border rounded bg-light'>
          <div className='d-flex align-items-center gap-2'>
            <IndeterminateCheckbox
              id='permission-bulk-global'
              checked={allVisibleSelected}
              indeterminate={someVisibleSelected}
              disabled={globalBulk.disabled || bulkCheckbox.disabled}
              title={
                searchQuery.trim()
                  ? 'Select or clear all permissions currently visible (filtered list)'
                  : 'Select or clear all permissions in the list'
              }
              onChange={toggleGlobalBulk}
            />
            <span className='small text-gray-800'>
              <span className='fw-semibold text-gray-900'>
                {globalBulk.selectedGlobalCount} / {globalBulk.totalCatalogCount}
              </span>
              <span className='text-muted'> {globalBulk.legend}</span>
            </span>
          </div>
          <div className='d-flex flex-wrap align-items-center gap-1'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              disabled={globalBulk.disabled || bulkCheckbox.disabled || visibleCodes.length === 0}
              title='Selects all permissions in the current list (filtered when searching)'
              onClick={applyVisibleSelectAll}
            >
              Select all
            </button>
            <button
              type='button'
              className='btn btn-sm btn-light'
              disabled={globalBulk.disabled || bulkCheckbox.disabled || visibleCodes.length === 0}
              title='Clears selection for all permissions in the current list (filtered when searching)'
              onClick={applyVisibleUnselectAll}
            >
              Unselect all
            </button>
          </div>
          {bulkScopeDescription ? (
            <span className='small text-primary ms-md-2' role='note'>
              {bulkScopeDescription}
            </span>
          ) : null}
        </div>
      )}

      {groups.map((section) => {
        const open = expanded.has(section.moduleKey)
        const idBase = `perm-grp-${section.moduleKey.replace(/\s+/g, '-').toLowerCase()}`
        if (overrideModuleBulk) {
          return (
            <GroupSectionWithOverrideBulk
              key={section.moduleKey}
              section={section}
              open={open}
              idBase={idBase}
              onToggleExpand={() => toggle(section.moduleKey)}
              getItemKey={getItemKey}
              renderItem={renderItem}
              renderGroupHeaderRight={renderGroupHeaderRight}
              config={overrideModuleBulk}
            />
          )
        }
        if (!bulkCheckbox) {
          return (
            <div key={section.moduleKey} className='border rounded overflow-hidden bg-white'>
              <button
                type='button'
                id={`${idBase}-hdr`}
                className='w-100 text-start btn btn-light d-flex align-items-center justify-content-between gap-2 py-2 px-3 border-0 rounded-0'
                onClick={() => toggle(section.moduleKey)}
                aria-expanded={open}
                aria-controls={`${idBase}-body`}
              >
                <span className='d-flex align-items-center flex-wrap gap-1 text-start'>
                  <span className='text-gray-500 flex-shrink-0' aria-hidden>
                    {open ? '▾' : '▸'}
                  </span>
                  <span className='fw-bold text-gray-900'>{section.moduleKey}</span>
                  {renderGroupHeaderRight ? (
                    <span className='small text-muted fw-normal'>— {renderGroupHeaderRight(section.moduleKey, section.items)}</span>
                  ) : (
                    <span className='small text-muted fw-normal'>— {section.items.length}</span>
                  )}
                </span>
              </button>
              {open && (
                <div id={`${idBase}-body`} role='region' aria-labelledby={`${idBase}-hdr`}>
                  {section.items.map((item) => (
                    <React.Fragment key={getItemKey(item)}>{renderItem(item)}</React.Fragment>
                  ))}
                </div>
              )}
            </div>
          )
        }
        return (
          <GroupSectionWithBulk
            key={section.moduleKey}
            section={section}
            open={open}
            idBase={idBase}
            onToggleExpand={() => toggle(section.moduleKey)}
            getItemKey={getItemKey}
            renderItem={renderItem}
            renderGroupHeaderRight={renderGroupHeaderRight}
            bulkCheckbox={bulkCheckbox}
          />
        )
      })}
    </div>
  )
}

type GroupSectionWithOverrideBulkProps<T> = {
  section: GroupedPermissionSection<T>
  open: boolean
  idBase: string
  onToggleExpand: () => void
  getItemKey: (item: T) => string
  renderItem: (item: T) => React.ReactNode
  renderGroupHeaderRight?: (moduleKey: string, items: T[]) => React.ReactNode
  config: PermissionOverrideModuleBulkConfig<T>
}

function GroupSectionWithOverrideBulk<T>({
  section,
  open,
  idBase,
  onToggleExpand,
  getItemKey,
  renderItem,
  renderGroupHeaderRight,
  config,
}: GroupSectionWithOverrideBulkProps<T>) {
  const allowN = section.items.filter((i) => config.getEffect(i) === 'ALLOW').length
  const denyN = section.items.filter((i) => config.getEffect(i) === 'DENY').length
  const items = section.items
  const dis = Boolean(config.disabled)

  return (
    <div className='border rounded overflow-hidden bg-white'>
      <div className='d-flex flex-wrap align-items-stretch'>
        <button
          type='button'
          className='btn btn-light d-flex align-items-center gap-2 flex-grow-1 text-start py-2 px-3 border-0 rounded-0'
          style={{minWidth: '8rem'}}
          id={`${idBase}-hdr`}
          aria-expanded={open}
          aria-controls={`${idBase}-body`}
          onClick={onToggleExpand}
        >
          <span className='text-gray-500 flex-shrink-0' aria-hidden>
            {open ? '▾' : '▸'}
          </span>
          <span className='d-flex flex-wrap align-items-baseline gap-1'>
            <span className='fw-bold text-gray-900'>{section.moduleKey}</span>
            <span className='small text-muted fw-normal'>
              — {items.length} override{items.length === 1 ? '' : 's'} · {allowN} allow · {denyN} deny
            </span>
          </span>
        </button>
        <div
          className='d-flex flex-wrap align-items-center gap-1 gap-sm-2 py-2 px-2 px-sm-3 border-top border-md-0 border-md-start bg-white'
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          role='presentation'
        >
          <button
            type='button'
            className='btn btn-sm btn-light-success py-0 px-2'
            disabled={dis || items.length === 0}
            title='Set effect to ALLOW for every row in this module (respects current filter)'
            onClick={() => config.onAllowAllVisibleInModule(items)}
          >
            Allow all
          </button>
          <button
            type='button'
            className='btn btn-sm btn-light py-0 px-2'
            disabled={dis || items.length === 0 || allowN === 0}
            title='Clear ALLOW in this module by switching those rows to DENY'
            onClick={() => config.onClearAllowInModule(items)}
          >
            Clear allow
          </button>
          <button
            type='button'
            className='btn btn-sm btn-light-danger py-0 px-2'
            disabled={dis || items.length === 0}
            title='Set effect to DENY for every row in this module (respects current filter)'
            onClick={() => config.onDenyAllVisibleInModule(items)}
          >
            Deny all
          </button>
          <button
            type='button'
            className='btn btn-sm btn-light py-0 px-2'
            disabled={dis || items.length === 0 || denyN === 0}
            title='Clear DENY in this module by switching those rows to ALLOW'
            onClick={() => config.onClearDenyInModule(items)}
          >
            Clear deny
          </button>
          {renderGroupHeaderRight ? (
            <span className='d-none d-xl-inline small text-gray-600 border-start ps-2 ms-1'>
              {renderGroupHeaderRight(section.moduleKey, section.items)}
            </span>
          ) : null}
        </div>
      </div>
      {open && (
        <div id={`${idBase}-body`} role='region' aria-labelledby={`${idBase}-hdr`}>
          {section.items.map((item) => (
            <React.Fragment key={getItemKey(item)}>{renderItem(item)}</React.Fragment>
          ))}
        </div>
      )}
    </div>
  )
}

type GroupSectionWithBulkProps<T> = {
  section: GroupedPermissionSection<T>
  open: boolean
  idBase: string
  onToggleExpand: () => void
  getItemKey: (item: T) => string
  renderItem: (item: T) => React.ReactNode
  renderGroupHeaderRight?: (moduleKey: string, items: T[]) => React.ReactNode
  bulkCheckbox: PermissionBulkCheckboxConfig<T>
}

function GroupSectionWithBulk<T>({
  section,
  open,
  idBase,
  onToggleExpand,
  getItemKey,
  renderItem,
  renderGroupHeaderRight,
  bulkCheckbox,
}: GroupSectionWithBulkProps<T>) {
  const moduleCodes = useMemo(() => {
    const codes: string[] = []
    const seen = new Set<string>()
    for (const item of section.items) {
      const c = bulkCheckbox.getItemCode(item).trim().toUpperCase()
      if (c && !seen.has(c)) {
        seen.add(c)
        codes.push(c)
      }
    }
    return codes
  }, [section.items, bulkCheckbox])

  const moduleSelected = moduleCodes.filter((c) => bulkCheckbox.isSelected(c)).length
  const allModuleSelected =
    moduleCodes.length > 0 && moduleSelected === moduleCodes.length
  const someModuleSelected = moduleSelected > 0 && !allModuleSelected

  const toggleModuleBulk = () => {
    if (bulkCheckbox.disabled) return
    if (allModuleSelected) {
      bulkCheckbox.onSetMany(moduleCodes, false)
    } else {
      bulkCheckbox.onSetMany(moduleCodes, true)
    }
  }

  const selectModule = () => {
    if (bulkCheckbox.disabled) return
    bulkCheckbox.onSetMany(moduleCodes, true)
  }

  const clearModule = () => {
    if (bulkCheckbox.disabled) return
    bulkCheckbox.onSetMany(moduleCodes, false)
  }

  return (
    <div className='border rounded overflow-hidden bg-white'>
      <div className='d-flex flex-wrap align-items-stretch'>
        <button
          type='button'
          className='btn btn-light d-flex align-items-center gap-2 flex-grow-1 text-start py-2 px-3 border-0 rounded-0'
          style={{minWidth: '8rem'}}
          id={`${idBase}-hdr`}
          aria-expanded={open}
          aria-controls={`${idBase}-body`}
          onClick={onToggleExpand}
        >
          <span className='text-gray-500 flex-shrink-0' aria-hidden>
            {open ? '▾' : '▸'}
          </span>
          <span className='d-flex flex-wrap align-items-baseline gap-1'>
            <span className='fw-bold text-gray-900'>{section.moduleKey}</span>
            <span className='small text-muted fw-normal'>
              — {moduleSelected} / {moduleCodes.length} {bulkCheckbox.moduleCountLegend}
            </span>
          </span>
        </button>
        <div
          className='d-flex flex-wrap align-items-center gap-2 py-2 px-3 border-top border-md-0 border-md-start bg-white'
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          role='presentation'
        >
          <div
            className='d-flex justify-content-center align-items-center flex-shrink-0'
            style={{width: '2.5rem', minWidth: '2.5rem'}}
          >
            <IndeterminateCheckbox
              id={`${idBase}-mod-cb`}
              checked={allModuleSelected}
              indeterminate={someModuleSelected}
              disabled={bulkCheckbox.disabled}
              style={{width: '1.05rem', height: '1.05rem'}}
              title='Select or clear all permissions in this module (respects current filter)'
              onChange={toggleModuleBulk}
            />
          </div>
          <button
            type='button'
            className='btn btn-link btn-sm py-0 px-1 text-nowrap'
            disabled={bulkCheckbox.disabled || moduleCodes.length === 0}
            title='Selects all permissions in this module'
            onClick={(e) => {
              e.preventDefault()
              selectModule()
            }}
          >
            Select module
          </button>
          <button
            type='button'
            className='btn btn-link btn-sm py-0 px-1 text-muted text-nowrap'
            disabled={bulkCheckbox.disabled || moduleCodes.length === 0}
            title='Clears selection for all permissions in this module'
            onClick={(e) => {
              e.preventDefault()
              clearModule()
            }}
          >
            Clear
          </button>
          {renderGroupHeaderRight ? (
            <span className='d-none d-xl-inline small text-gray-600 border-start ps-2 ms-1'>
              {renderGroupHeaderRight(section.moduleKey, section.items)}
            </span>
          ) : null}
        </div>
      </div>
      {open && (
        <div id={`${idBase}-body`} role='region' aria-labelledby={`${idBase}-hdr`}>
          {section.items.map((item) => (
            <React.Fragment key={getItemKey(item)}>{renderItem(item)}</React.Fragment>
          ))}
        </div>
      )}
    </div>
  )
}
